import type { Permission } from '../rbac.js';

/**
 * Report definitions: the single registry both the API and the web client read.
 *
 * Nothing else in the codebase may hold a report's name, category or position.
 */

export const REPORT_CATEGORIES = [
  { id: 'business_overview', name: 'Business Overview' },
  { id: 'sales', name: 'Sales' },
  { id: 'receivables', name: 'Receivables' },
  { id: 'payments_received', name: 'Payments Received' },
  { id: 'inventory', name: 'Inventory' },
  { id: 'payables_purchases', name: 'Payables and Purchases' },
  { id: 'purchases_expenses', name: 'Purchases and Expenses' },
  { id: 'accountant', name: 'Accountant' },
  { id: 'projects_timesheets', name: 'Projects and Timesheets' },
  { id: 'recurring_transactions', name: 'Recurring Transactions' },
  { id: 'taxes', name: 'Taxes and Tax Compliance' },
  { id: 'activity', name: 'Activity' },
  /** WHNUNIVERSE's own reports. Listed after the standard categories and never counted among the 81. */
  { id: 'whn_books', name: 'WHNUNIVERSE Books' },
] as const;
export type ReportCategoryId = (typeof REPORT_CATEGORIES)[number]['id'];

/** Every filter a report may accept. A definition lists the subset it supports. */
export const REPORT_FILTER_KEYS = [
  'period', // from / to, or a preset
  'asOf',
  'customerId',
  'vendorId',
  'itemName',
  'itemListId',
  'invoiceType', // NORMAL | GST | ALL
  'status',
  'paymentStatus',
  'expenseCategory',
  'expenseReportStatus',
  'approverId',
  'cashOfficerId',
  'reimbursementStatus',
  'bankAccountId',
  'ledgerAccountId',
  'accountType',
  'currency',
  'salesPersonId',
  'claimantId',
  'transactionType',
  'taxRate',
  'entityType',
  'actorId',
  'search',
  'groupBy',
  'compare',
  'ageInterval',
  'includeAdjustmentPeriod',
] as const;
export type ReportFilterKey = (typeof REPORT_FILTER_KEYS)[number];

export const REPORT_STATUSES = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'IMPLEMENTED',
  'TESTED',
  'BLOCKED_BY_MISSING_MODULE',
  'DISABLED_BY_REGION',
] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];

export type ReportColumnType = 'text' | 'money' | 'number' | 'percent' | 'date' | 'status';

export interface ReportColumnDef {
  key: string;
  label: string;
  type: ReportColumnType;
}

export const CATALOGUE_EXPORT_FORMATS = ['csv', 'xlsx', 'pdf'] as const;
export type CatalogueExportFormat = (typeof CATALOGUE_EXPORT_FORMATS)[number];

export interface CatalogueReport {
  id: string;
  name: string;
  category: ReportCategoryId;
  /** 1-based position of the category in the navigation. */
  categoryPosition: number;
  /** 1-based position of the report inside its category. */
  position: number;
  /** 1-81 across the whole standard catalogue; 0 for WHNUNIVERSE-specific reports. */
  catalogueNumber: number;
  description: string;
  methodology: string;
  /** Modules whose records feed the report. */
  sources: string[];
  /** Permissions the viewer must hold, each at organisation-wide scope. */
  permissions: Permission[];
  filters: ReportFilterKey[];
  columns: ReportColumnDef[];
  groupings?: string[];
  drilldown: string;
  orientation: 'portrait' | 'landscape';
  exports: CatalogueExportFormat[];
  /** The report can be compared with a previous period or year. */
  comparable?: boolean;
  /** Jurisdictions the report is valid for; absent means all. */
  regions?: string[];
  /** Why the report cannot run yet, for BLOCKED_BY_MISSING_MODULE. */
  blockedReason?: string;
  /** Why it is disabled, for DISABLED_BY_REGION. */
  regionNote?: string;
  status: ReportStatus;
  /** Test files that exercise this report, relative to apps/api/test. */
  testRef: string[];
  /** Zoho Books equivalent grouping/label when WHNUNIVERSE names it differently. */
  mapping?: string;
}
